import '../styles.scss';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Parser from 'html-react-parser';
import { Formik, FormikHelpers } from 'formik';
import { Button } from '@mui/material';
import Card from '../../../../common/card';
import Input from '../../../../common/input';
import OnboardingDialog from '../../../../common/onboarding-dialog';
import TooltipIcon from '../../../../common/tooltip-icon';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../../../config/routes';
import l from '../../../../../lang';
import { usePayouts } from '../../../../../store/payouts';
import { useHome } from '../../../../../store/home';
import { PayoutCreationStep, FlowType } from '../../../../../store/payouts/interfaces';
import {
  applyPayoutAmountToFixed,
  getBeneficiaryDisplayName,
  getPayoutWizardMenuItems,
  getPayoutCurrencyOptions,
} from '../utils';
import { ReactComponent as BriefcaseIcon } from '../../../../../assets/icons/briefcase.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/info-unfilled.svg';
import PayoutCurrencySelect from './payout-currency-select';

interface PayoutDataFormValues {
  recipientAmount: number | string;
  youPayAmount: number | string;
  currency: string;
  description: string;
}

interface PayoutDataFormProps {
  initialValues: PayoutDataFormValues;
  transferCountry: string;
  currencyOptions: Record<string, string>;
  balanceCurrency: string;
  availableBalance: number;
  beneficiaryName: string;
  feeAmount: number;
  feeCurrency: string;
  feeExchangeRate?: number;
  totalDebitAmount?: number;
  youPayAmountFromFee?: number;
  onChangeBeneficiary: () => void;
  onCancel: () => void;
  onSubmit: (values: PayoutDataFormValues) => void;
  getPayoutFee: (amount: number, currency: string, country: string) => void;
}

const PayoutDataForm = ({
  initialValues,
  transferCountry,
  currencyOptions,
  balanceCurrency,
  availableBalance,
  beneficiaryName,
  feeAmount,
  feeCurrency,
  feeExchangeRate,
  totalDebitAmount,
  youPayAmountFromFee,
  onChangeBeneficiary,
  onCancel,
  onSubmit,
  getPayoutFee,
}: PayoutDataFormProps) => {
  const feeDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const lastEditedFieldRef = useRef<'recipient' | 'youPay'>('recipient');

  const scheduleFeeRequest = useCallback(
    (amount: number, currency: string) => {
      if (feeDebounceRef.current) {
        clearTimeout(feeDebounceRef.current);
      }
      if (!amount || !currency || !transferCountry) {
        return;
      }
      feeDebounceRef.current = setTimeout(() => {
        getPayoutFee(amount, currency, transferCountry);
      }, 500);
    },
    [getPayoutFee, transferCountry]
  );

  useEffect(() => {
    return () => {
      if (feeDebounceRef.current) {
        clearTimeout(feeDebounceRef.current);
      }
    };
  }, []);

  const isCrossCurrency = (currency: string) => currency && currency !== balanceCurrency;

  return (
    <Formik
      initialValues={initialValues}
      validate={values => {
        const errors: Record<string, string> = {};
        const recipientAmount = Number(values.recipientAmount);

        if (!values.recipientAmount || recipientAmount <= 0) {
          errors.recipientAmount = l('form.input.required');
        }
        if (!values.currency) {
          errors.currency = l('form.input.required');
        }
        if (!values.description) {
          errors.description = l('form.input.required');
        }

        const totalDebit =
          totalDebitAmount || Number(youPayAmountFromFee || values.youPayAmount || 0) + Number(feeAmount || 0);

        if (recipientAmount > 0 && totalDebit > availableBalance) {
          errors.recipientAmount = l('payouts.create.payoutData.insufficientBalance');
        }

        return errors;
      }}
      onSubmit={(values: PayoutDataFormValues) => onSubmit(values)}
    >
      {({ values, errors, touched, setFieldValue, handleBlur, handleSubmit, setFieldTouched }) => {
        const showDualAmounts = isCrossCurrency(values.currency);

        return (
          <PayoutDataFormFields
            values={values}
            errors={errors}
            touched={touched}
            showDualAmounts={showDualAmounts}
            transferCountry={transferCountry}
            currencyOptions={currencyOptions}
            balanceCurrency={balanceCurrency}
            availableBalance={availableBalance}
            beneficiaryName={beneficiaryName}
            feeAmount={feeAmount}
            feeCurrency={feeCurrency}
            feeExchangeRate={Number(feeExchangeRate || 0)}
            youPayAmountFromFee={youPayAmountFromFee}
            onChangeBeneficiary={onChangeBeneficiary}
            onCancel={onCancel}
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            getPayoutFee={getPayoutFee}
            scheduleFeeRequest={scheduleFeeRequest}
            lastEditedFieldRef={lastEditedFieldRef}
          />
        );
      }}
    </Formik>
  );
};

interface PayoutDataFormFieldsProps {
  values: PayoutDataFormValues;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  showDualAmounts: boolean;
  transferCountry: string;
  currencyOptions: Record<string, string>;
  balanceCurrency: string;
  availableBalance: number;
  beneficiaryName: string;
  feeAmount: number;
  feeCurrency: string;
  feeExchangeRate: number;
  youPayAmountFromFee?: number;
  onChangeBeneficiary: () => void;
  onCancel: () => void;
  handleSubmit: () => void;
  handleBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setFieldValue: FormikHelpers<PayoutDataFormValues>['setFieldValue'];
  setFieldTouched: FormikHelpers<PayoutDataFormValues>['setFieldTouched'];
  getPayoutFee: (amount: number, currency: string, country: string) => void;
  scheduleFeeRequest: (amount: number, currency: string) => void;
  lastEditedFieldRef: MutableRefObject<'recipient' | 'youPay'>;
}

const PayoutDataFormFields = ({
  values,
  errors,
  touched,
  showDualAmounts,
  transferCountry,
  currencyOptions,
  balanceCurrency,
  availableBalance,
  beneficiaryName,
  feeAmount,
  feeCurrency,
  feeExchangeRate,
  youPayAmountFromFee,
  onChangeBeneficiary,
  onCancel,
  handleSubmit,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  getPayoutFee,
  scheduleFeeRequest,
  lastEditedFieldRef,
}: PayoutDataFormFieldsProps) => {
  const displayRateCurrencyRef = useRef('');
  const [displayRate, setDisplayRate] = useState(0);
  const hasAmountInput = Boolean(values.recipientAmount || values.youPayAmount);

  useEffect(() => {
    if (displayRateCurrencyRef.current !== values.currency) {
      displayRateCurrencyRef.current = values.currency;
      lastEditedFieldRef.current = 'recipient';
      setDisplayRate(0);
      return;
    }
  }, [lastEditedFieldRef, values.currency]);

  useEffect(() => {
    if (!showDualAmounts || !feeExchangeRate || !values.currency || !hasAmountInput) {
      return;
    }

    if (displayRateCurrencyRef.current !== values.currency) {
      return;
    }

    if (!displayRate) {
      setDisplayRate(feeExchangeRate);
    }

    if (lastEditedFieldRef.current === 'recipient') {
      if (youPayAmountFromFee !== undefined && values.recipientAmount) {
        setFieldValue('youPayAmount', applyPayoutAmountToFixed(youPayAmountFromFee));
      }
      return;
    }

    if (lastEditedFieldRef.current === 'youPay' && values.youPayAmount) {
      const derivedRecipientAmount = applyPayoutAmountToFixed(Number(values.youPayAmount) * feeExchangeRate);
      const needsFeeUpdate = String(values.recipientAmount) !== String(derivedRecipientAmount);

      setFieldValue('recipientAmount', derivedRecipientAmount);

      if (needsFeeUpdate) {
        scheduleFeeRequest(Number(derivedRecipientAmount), values.currency);
      }
    }
  }, [
    displayRate,
    feeExchangeRate,
    hasAmountInput,
    lastEditedFieldRef,
    scheduleFeeRequest,
    setFieldValue,
    showDualAmounts,
    values.currency,
    values.recipientAmount,
    values.youPayAmount,
    youPayAmountFromFee,
  ]);

  return (
    <form className="payout-create__payout-data-form" onSubmit={handleSubmit}>
      <div className="payout-create__payment-summary">
        <div className="payout-create__payment-summary__section">
          <span className="payout-create__payment-summary__label">{l('payouts.create.payoutData.payFrom')}</span>
          <div className="payout-create__payment-summary__label-group">
            <span className="payout-create__payment-summary__subtext">
              {l('payouts.create.payoutData.availableBalance')}
            </span>
            <TooltipIcon
              placement="bottom-end"
              text={Parser(l('payouts.create.payoutData.availableBalanceTooltip'))}
              icon={<InfoIcon />}
              reducedPadding
            />
          </div>
          <span className="payout-create__payment-summary__balance-line">
            {l('payouts.create.payoutData.availableBalance')} {availableBalance} {balanceCurrency}
          </span>
        </div>
        <div className="payout-create__payment-summary__section">
          <div className="payout-create__payment-summary__label-row">
            <span className="payout-create__payment-summary__label">{l('payouts.create.payoutData.payTo')}</span>
            <button type="button" className="payout-create__change-link" onClick={onChangeBeneficiary}>
              {l('payouts.create.payoutData.changeBeneficiary')}
            </button>
          </div>
          <span className="payout-create__payment-summary__value">{beneficiaryName}</span>
        </div>
      </div>

      <PayoutCurrencySelect
        name="currency"
        value={values.currency}
        transferCountry={transferCountry}
        currencyOptions={currencyOptions}
        defaultOptionText={l('payouts.create.field.selectPlaceholder')}
        error={touched.currency ? errors.currency : ''}
        onBlur={handleBlur}
        onChange={newCurrency => {
          setFieldValue('currency', newCurrency);
          setFieldValue('recipientAmount', '');
          setFieldValue('youPayAmount', '');
          setFieldTouched('recipientAmount', false);
          setFieldTouched('youPayAmount', false);
        }}
      />

      <div className={`payout-create__amount-row ${showDualAmounts ? 'payout-create__amount-row--dual' : ''}`}>
        {(() => {
          const amountError = touched.recipientAmount ? errors.recipientAmount : '';
          const showAmountErrorBorder = Boolean(amountError);

          return (
            <>
              <div className="payout-create__amount-field">
                <Input
                  type="number"
                  name="recipientAmount"
                  label={
                    showDualAmounts ? l('payouts.create.payoutData.recipientReceives') : l('payouts.create.field.amount')
                  }
                  value={values.recipientAmount}
                  error={showDualAmounts ? showAmountErrorBorder : amountError}
                  onBlur={event => {
                    handleBlur(event);
                    if (values.recipientAmount && values.currency) {
                      getPayoutFee(Number(values.recipientAmount), values.currency, transferCountry);
                    }
                  }}
                  onChange={event => {
                    const recipientValue = event.target.value;
                    lastEditedFieldRef.current = 'recipient';
                    setFieldValue('recipientAmount', recipientValue);

                    if (!Number(recipientValue)) {
                      setFieldValue('youPayAmount', '');
                      return;
                    }

                    if (!showDualAmounts) {
                      setFieldValue('youPayAmount', recipientValue);
                    } else if (displayRate) {
                      setFieldValue('youPayAmount', applyPayoutAmountToFixed(Number(recipientValue) / displayRate));
                    }

                    scheduleFeeRequest(Number(recipientValue), values.currency);
                  }}
                  prefix={values.currency}
                  disabled={!values.currency}
                  shrink={Boolean(values.currency)}
                />
              </div>

              {showDualAmounts && (
                <div className="payout-create__amount-field">
                  <Input
                    type="number"
                    name="youPayAmount"
                    label={l('payouts.create.payoutData.youPay')}
                    value={values.youPayAmount}
                    error={showAmountErrorBorder}
                    onBlur={event => {
                      handleBlur(event);
                      if (!values.youPayAmount || !values.currency) {
                        return;
                      }

                      const derivedRecipientAmount = displayRate
                        ? Number(values.youPayAmount) * displayRate
                        : Number(values.recipientAmount);

                      if (derivedRecipientAmount > 0) {
                        getPayoutFee(derivedRecipientAmount, values.currency, transferCountry);
                      }
                    }}
                    onChange={event => {
                      const youPayValue = event.target.value;
                      lastEditedFieldRef.current = 'youPay';
                      setFieldValue('youPayAmount', youPayValue);

                      if (!Number(youPayValue)) {
                        setFieldValue('recipientAmount', '');
                        return;
                      }

                      if (!displayRate) {
                        scheduleFeeRequest(1, values.currency);
                        return;
                      }

                      const derivedRecipientAmount = Number(youPayValue) * displayRate;
                      setFieldValue('recipientAmount', applyPayoutAmountToFixed(derivedRecipientAmount));
                      scheduleFeeRequest(derivedRecipientAmount, values.currency);
                    }}
                    prefix={balanceCurrency}
                    shrink
                  />
                </div>
              )}

              {showDualAmounts && amountError && typeof amountError === 'string' && (
                <p className="payout-create__amount-row-error">{amountError}</p>
              )}
            </>
          );
        })()}
      </div>

      {/* TODO: re-enable exchange rate display once FX sync is stable
      {showDualAmounts && displayRate > 0 && (
        <p className="payout-create__exchange-rate">
          <span>{l('payouts.create.payoutData.exchangeRate')}</span>
          <span className="payout-create__exchange-rate__value">{balanceCurrency} 1.00</span>
          <ArrowRightIcon />
          <span className="payout-create__exchange-rate__value">
            {values.currency} {applyPayoutAmountToFixed(displayRate)}
          </span>
        </p>
      )}
      */}

      <Input
        name="description"
        label={l('payouts.create.payoutData.transactionDescription')}
        placeholder={l('payouts.create.payoutData.descriptionPlaceholder')}
        value={values.description}
        error={touched.description ? errors.description : ''}
        onBlur={handleBlur}
        onChange={event => setFieldValue('description', event.target.value)}
      />

      <div className="payout-create__fee-row">
        <div className="payout-create__fee-row__left">
          <span className="payout-create__fee-row__title">{l('payouts.create.payoutData.totalFees')}</span>
          <span className="payout-create__fee-row__subtitle">
            {l('payouts.create.payoutData.feesIncludedSubtitle', feeCurrency || balanceCurrency)}
            <TooltipIcon
              placement="bottom-end"
              text={Parser(l('payouts.create.payoutData.feesIncludedTooltip'))}
              icon={<InfoIcon />}
              reducedPadding
            />
          </span>
        </div>
        <span className="payout-create__fee-row__amount">
          {feeAmount || '0.00'} {feeCurrency || balanceCurrency}
        </span>
      </div>

      <div className="payout-create__buttons">
        <Button
          variant="contained"
          size="large"
          onClick={onCancel}
          sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
        >
          {l('common.cancel')}
        </Button>
        <Button variant="contained" size="large" type="submit">
          {l('payouts.create.payoutData.continue')}
        </Button>
      </div>
    </form>
  );
};

const PayoutData = () => {
  const history = useHistory();
  const {
    createPayout,
    setCreatePayoutData,
    setCreatePayoutStep,
    setCreatePayoutSubView,
    getPayoutFee,
    getPayoutCountryConfigurations,
  } = usePayouts();
  const { balances, getBalances } = useHome();

  const transferCountry = createPayout.createPayoutData.transferCountry || '';
  const flowType = createPayout.createPayoutData.flowType || FlowType.B2C;
  const balanceCurrency = balances.data?.currency || 'USD';
  const availableBalance = Number(balances.data?.availableBalance || 0);
  const feeExchangeRate = Number(createPayout.fee.data?.exchangeRate || 0);
  const beneficiaryName = getBeneficiaryDisplayName(createPayout.createPayoutData);

  const currencyOptions = useMemo(
    () => getPayoutCurrencyOptions(createPayout.enabledCountryConfigurations.data, transferCountry, flowType),
    [createPayout.enabledCountryConfigurations.data, transferCountry, flowType]
  );

  const initialValues = useMemo<PayoutDataFormValues>(() => {
    const recipientAmount = createPayout.createPayoutData.transferAmount || '';
    const currency = createPayout.createPayoutData.currencyToPay || '';
    let youPayAmount: number | string = '';

    if (recipientAmount && currency) {
      if (currency === balanceCurrency) {
        youPayAmount = recipientAmount;
      } else if (createPayout.fee.data?.youPayAmount !== undefined) {
        youPayAmount = applyPayoutAmountToFixed(createPayout.fee.data.youPayAmount);
      } else {
        youPayAmount = '';
      }
    }

    return {
      recipientAmount,
      youPayAmount,
      currency,
      description: createPayout.createPayoutData.description || '',
    };
  }, [
    balanceCurrency,
    createPayout.createPayoutData.currencyToPay,
    createPayout.createPayoutData.description,
    createPayout.createPayoutData.transferAmount,
    createPayout.fee.data?.youPayAmount,
  ]);

  useEffect(() => {
    if (createPayout.step < PayoutCreationStep.ACCOUNT_DATA) {
      history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA);
      return;
    }
    setCreatePayoutStep(PayoutCreationStep.PAYOUT_DATA);
    getBalances();
    if (!createPayout.enabledCountryConfigurations.data.length) {
      getPayoutCountryConfigurations();
    }
  }, []);

  useEffect(() => {
    const selectedCurrency = createPayout.createPayoutData.currencyToPay;
    if (selectedCurrency && !currencyOptions[selectedCurrency]) {
      setCreatePayoutData({ currencyToPay: undefined });
    }
  }, [currencyOptions, createPayout.createPayoutData.currencyToPay, setCreatePayoutData]);

  const handleChangeBeneficiary = () => {
    setCreatePayoutSubView('bank-data');
    history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA);
  };

  const handleSubmit = (values: PayoutDataFormValues) => {
    setCreatePayoutData({
      transferAmount: Number(values.recipientAmount),
      currencyToPay: values.currency,
      description: values.description,
    });
    setCreatePayoutStep(PayoutCreationStep.REVIEW);
    history.push(Routes.PAYOUTS_STEP_REVIEW);
  };

  return (
    <OnboardingDialog
      dividers={false}
      unpaddingContent
      unpaddingFooter
      fullScreenMobile
      type="md"
      bgColor="#F9FAFB"
      title={`${l('payouts.create.title')} | ${l('payouts.create.payoutData.menuTitle')}`}
      handleClose={() => history.push(Routes.PAYOUTS)}
      body={
        <div className="payout-create__layout">
          <Card>
            <div className="payout-create__card-content">
              <h2>{l('payouts.create.payoutData.title')}</h2>
              <br />
              <div className="transaction_detail__title_section">
                <BriefcaseIcon className="transaction_detail__title_section__icon" />
                {l('payouts.create.payoutData.title')}
              </div>
              <p className="payout-create__subtitle">{l('payouts.create.payoutData.description')}</p>
              <PayoutDataForm
                initialValues={initialValues}
                transferCountry={transferCountry}
                currencyOptions={currencyOptions}
                balanceCurrency={balanceCurrency}
                availableBalance={availableBalance}
                beneficiaryName={beneficiaryName}
                feeAmount={createPayout.fee.data?.feeAmount || 0}
                feeCurrency={createPayout.fee.data?.feeCurrency || balanceCurrency}
                feeExchangeRate={feeExchangeRate}
                totalDebitAmount={createPayout.fee.data?.totalDebitAmount}
                youPayAmountFromFee={createPayout.fee.data?.youPayAmount}
                onChangeBeneficiary={handleChangeBeneficiary}
                onCancel={() => history.push(Routes.PAYOUTS_STEP_ACCOUNT_DATA)}
                onSubmit={handleSubmit}
                getPayoutFee={getPayoutFee}
              />
            </div>
          </Card>
        </div>
      }
      menuItems={getPayoutWizardMenuItems('payout')}
    />
  );
};

export default PayoutData;
